import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useRef, useState, type ComponentType } from 'react';
import { FaLaptopMedical } from 'react-icons/fa';
import {
  Building2,
  CircleAlert,
  CircleCheckBig,
  Clock3,
  FlaskConical,
  GraduationCap,
  MapPinned,
  ScanLine,
  TrendingUp,
  Users,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import SiteLayout from '@/components/site/SiteLayout';
import CoverageRequestForm from '@/components/company_pages/CoverageRequestForm';
import { infoEmail } from '@/constants/emails';

type MarqueeItem = {
  name: string;
  src?: string;
};

type SnapshotItem = {
  value: string;
  label: string;
  countTo?: number;
  detail: string;
  accent?: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  displayValue?: string;
};

type MetricItem = SnapshotItem & {
  icon: LucideIcon;
  tone: 'blue' | 'gold';
};

type SectionIcon = ComponentType<{ className?: string }>;

type MetricGroup = {
  title: string;
  badge: string;
  body: string;
  icon: SectionIcon;
  items: MetricItem[];
};

type AudienceOption = {
  id: 'radiology' | 'infusion' | 'residents';
  badge: string;
  title: string;
  description: string;
  points: string[];
  ctaLabel: string;
  href: string;
  icon: LucideIcon;
};

type HeroDashboardCard = {
  label: string;
  value: string;
  detail: string;
  icon: LucideIcon;
  tone: 'gold' | 'violet' | 'blue' | 'cyan' | 'goldGlow';
  wide?: boolean;
  countTo?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  displayValue?: string;
};

type ComparisonRow = {
  category: string;
  docMoonlight: string;
  others: string;
};

type ComparisonSection = {
  title: string;
  icon: SectionIcon;
  tone: 'gold' | 'blue';
  rows: ComparisonRow[];
};

const marqueeItems: MarqueeItem[] = [
  { name: 'Lumexa Imaging', src: '/images/lumexa-imaging.svg' },
  { name: 'Radiology Partners', src: '/images/RP.svg' },
  { name: 'Rad Clinic', src: '/images/the-radiology-clinic-logo.svg' },
  {
    name: 'Jefferson Radiology',
    src: '/images/jefferson-radiology-logo.svg',
  },
  {
    name: 'Hackensack Radiology Group',
    src: '/images/hrg-imaging-logo.webp',
  },
  { name: 'Premier Radiology', src: '/images/premier-radiology.png' },
  {
    name: 'American Health Imaging',
    src: '/images/american-health-imaging.svg',
  },
  {
    name: 'Touchstone Medical Imaging',
    src: '/images/touchstone-medical-imaging.png',
  },
];

const trainingProgramItems: MarqueeItem[] = [
  { name: 'Alabama' },
  { name: 'Arkansas' },
  { name: 'Arizona' },
  { name: 'California' },
  { name: 'Colorado' },
  { name: 'Connecticut' },
  { name: 'District of Columbia' },
  { name: 'Florida' },
  { name: 'Georgia' },
  { name: 'Iowa' },
  { name: 'Illinois' },
  { name: 'Indiana' },
  { name: 'Kansas' },
  { name: 'Kentucky' },
  { name: 'Louisiana' },
  { name: 'Massachusetts' },
  { name: 'Maryland' },
  { name: 'Michigan' },
  { name: 'Minnesota' },
  { name: 'Missouri' },
  { name: 'Mississippi' },
  { name: 'North Carolina' },
  { name: 'North Dakota' },
  { name: 'New Hampshire' },
  { name: 'New Jersey' },
  { name: 'Nevada' },
  { name: 'New York' },
  { name: 'Ohio' },
  { name: 'Oklahoma' },
  { name: 'Oregon' },
  { name: 'Pennsylvania' },
  { name: 'Puerto Rico' },
  { name: 'Rhode Island' },
  { name: 'South Carolina' },
  { name: 'Tennessee' },
  { name: 'Texas' },
  { name: 'Utah' },
  { name: 'Virginia' },
  { name: 'Vermont' },
  { name: 'Washington' },
  { name: 'Wisconsin' },
  { name: 'West Virginia' },
];

const audienceOptions: AudienceOption[] = [
  {
    id: 'radiology',
    badge: 'Virtual Coverage',
    title: 'Virtual Coverage',
    description:
      'Secure virtual physician coverage for imaging centers when permitted by CMS, state regulations, and facility policy.',
    points: [
      'Virtual supervision where permitted',
      'Real-time contrast guidance',
      'More reliable imaging schedules',
    ],
    ctaLabel: 'Explore Virtual',
    href: '/virtual-coverage',
    icon: ScanLine,
  },
  {
    id: 'infusion',
    badge: 'Onsite Coverage',
    title: 'Onsite Coverage',
    description:
      'Licensed physician coverage at your facility for contrast supervision, medication support, and emergency response when needed.',
    points: [
      'ACLS-trained physicians onsite',
      'Expandable AM coverage',
      'As-needed, pay-as-you-go coverage',
    ],
    ctaLabel: 'Explore Onsite',
    href: '/onsite-coverage',
    icon: FlaskConical,
  },
  {
    id: 'residents',
    badge: 'Hybrid + Teleradiology',
    title: 'Hybrid + Teleradiology',
    description:
      "A flexible coverage model combining onsite, virtual support, and coordinated teleradiology reading based on your center's needs.",
    points: [
      'Customized coverage model',
      'Diagnostic and subspecialty reads',
      'Coverage and reading support',
    ],
    ctaLabel: 'Explore Hybrid',
    href: '/hybrid-coverage',
    icon: UserRound,
  },
];

const heroDashboardCards: HeroDashboardCard[] = [
  {
    label: 'Doctor Network',
    value: '3,500+',
    detail: 'Licensed Residents',
    icon: Users,
    tone: 'gold',
    countTo: 3500,
    suffix: '+',
  },
  {
    label: 'Facility Coverage',
    value: '100%',
    detail: 'Days, Nights & Holidays',
    icon: MapPinned,
    tone: 'violet',
    countTo: 100,
    suffix: '%',
  },
  {
    label: 'Doctor Network',
    value: '155+',
    detail: 'Training Programs',
    icon: GraduationCap,
    tone: 'blue',
    countTo: 155,
    suffix: '+',
  },
  {
    label: 'Facility Coverage',
    value: '<15 sec',
    detail: 'Response Time',
    icon: Clock3,
    tone: 'cyan',
    countTo: 15,
    prefix: '<',
    suffix: ' sec',
  },
  {
    label: 'Doctor Network',
    value: '$120K',
    detail: 'Earning Potential',
    icon: TrendingUp,
    tone: 'goldGlow',
    wide: true,
    countTo: 120,
    prefix: '$',
    suffix: 'K',
  },
  {
    label: 'Facility Coverage',
    value: 'STAT',
    detail: 'Reading Support',
    icon: Building2,
    tone: 'goldGlow',
    wide: true,
    displayValue: 'STAT',
  },
];

const metricGroups: MetricGroup[] = [
  {
    title: 'Doctor Network',
    badge: 'Doctor Network',
    body: 'Licensed physicians, verified credentials, and an expanding coverage network built for dependable staffing.',
    icon: Users,
    items: [
      {
        value: '3,500+',
        countTo: 3500,
        suffix: '+',
        label: 'Licensed Residents',
        detail: 'Licensed Residents',
        icon: Users,
        tone: 'blue',
      },
      {
        value: '155+',
        countTo: 155,
        suffix: '+',
        label: 'Training Programs',
        detail: 'Training Programs',
        icon: GraduationCap,
        tone: 'blue',
      },
      {
        value: '$120K',
        countTo: 120,
        prefix: '$',
        suffix: 'K',
        label: 'Earning Potential',
        detail: 'Earning Potential',
        icon: TrendingUp,
        tone: 'blue',
      },
    ],
  },
  {
    title: 'Facility Coverage',
    badge: 'Facility Coverage',
    body: 'Fast response, multi-state reach, and coverage support designed for imaging and infusion operations.',
    icon: Building2,
    items: [
      {
        value: '100%',
        countTo: 100,
        suffix: '%',
        label: 'Holiday Coverage',
        detail: 'Days, Nights & Holidays',
        icon: MapPinned,
        tone: 'gold',
      },
      {
        value: '<15 sec',
        countTo: 15,
        prefix: '<',
        suffix: ' sec',
        label: 'Response Time',
        detail: 'Response Time',
        icon: Clock3,
        tone: 'gold',
      },
      {
        value: 'STAT',
        displayValue: 'STAT',
        label: 'Reading Support',
        detail: 'Reading Support',
        icon: Building2,
        tone: 'gold',
      },
    ],
  },
];

const comparisonSections: ComparisonSection[] = [
  {
    title: 'Coverage & Operations',
    icon: Building2,
    tone: 'gold',
    rows: [
      {
        category: 'Fill Rate',
        docMoonlight: '98%+ with algorithmic matching',
        others: 'Unpredictable manual calls',
      },
      {
        category: 'Response Time',
        docMoonlight: 'Minutes via automated alerts',
        others: 'Days waiting for callbacks',
      },
      {
        category: 'Credentialing',
        docMoonlight: 'Pre-verified digital wallets',
        others: 'Slow paper-based process',
      },
    ],
  },
  {
    title: 'Technology & Workflow',
    icon: FaLaptopMedical,
    tone: 'blue',
    rows: [
      {
        category: 'Platform Access',
        docMoonlight: 'Real-time web & mobile dashboards',
        others: 'Emails and spreadsheets',
      },
      {
        category: 'Time Tracking',
        docMoonlight: 'GPS-verified digital timesheets',
        others: 'Manual paper timesheets',
      },
      {
        category: 'Analytics',
        docMoonlight: 'Live reporting on all metrics',
        others: 'Delayed monthly reports',
      },
    ],
  },
  {
    title: 'Support & Physician Experience',
    icon: Users,
    tone: 'gold',
    rows: [
      {
        category: 'Quality of Care',
        docMoonlight: 'Curated top-tier physicians',
        others: 'Volume-based inconsistent quality',
      },
      {
        category: 'Support Team',
        docMoonlight: '24/7 medical director support',
        others: 'Business hours call center',
      },
    ],
  },
];

const comparisonRows: ComparisonRow[] = comparisonSections.flatMap(
  (section) => section.rows,
);

const homeStructuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'DocMoonlight',
    url: 'https://www.docmoonlight.com',
    logo: 'https://www.docmoonlight.com/images/logo.svg',
    founder: {
      '@type': 'Person',
      name: 'Ramez Morcos, MD, MBA',
      jobTitle: 'Founder, CEO',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'coverage requests',
        url: 'https://www.docmoonlight.com/contact',
        email: infoEmail,
      },
    ],
    sameAs: [
      'https://apps.apple.com/us/app/docmoonlight/id6448665833',
      'https://play.google.com/store/apps/details?id=com.docmoonlight',
    ],
    knowsAbout: [
      'physician coverage',
      'contrast supervision',
      'virtual physician supervision',
      'onsite physician coverage',
      'hybrid physician coverage',
      'teleradiology support',
      'resident moonlighting',
      'infusion center physician oversight',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'DocMoonlight physician coverage services',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Service',
          name: 'Virtual physician coverage',
          url: 'https://www.docmoonlight.com/virtual-coverage',
          serviceType: 'Virtual physician supervision',
          provider: { '@type': 'Organization', name: 'DocMoonlight' },
          audience: {
            '@type': 'Audience',
            audienceType: 'Outpatient imaging centers and radiology groups',
          },
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Service',
          name: 'Onsite physician coverage',
          url: 'https://www.docmoonlight.com/onsite-coverage',
          serviceType: 'Onsite physician coverage',
          provider: { '@type': 'Organization', name: 'DocMoonlight' },
          audience: {
            '@type': 'Audience',
            audienceType: 'Imaging centers and infusion centers',
          },
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Service',
          name: 'Hybrid physician coverage',
          url: 'https://www.docmoonlight.com/hybrid-coverage',
          serviceType: 'Hybrid onsite and virtual physician coverage',
          provider: { '@type': 'Organization', name: 'DocMoonlight' },
        },
      },
      {
        '@type': 'ListItem',
        position: 4,
        item: {
          '@type': 'Service',
          name: 'Teleradiology support',
          url: 'https://www.docmoonlight.com/teleradiology',
          serviceType: 'Teleradiology reading support',
          provider: { '@type': 'Organization', name: 'DocMoonlight' },
        },
      },
      {
        '@type': 'ListItem',
        position: 5,
        item: {
          '@type': 'Service',
          name: 'Infusion center physician oversight',
          url: 'https://www.docmoonlight.com/infusion',
          serviceType: 'Infusion center physician coverage',
          provider: { '@type': 'Organization', name: 'DocMoonlight' },
        },
      },
    ],
  },
];

const formatCountValue = (
  value: number,
  options: {
    prefix?: string;
    suffix?: string;
    decimals?: number;
  },
) => {
  const formatted = options.decimals
    ? value.toFixed(options.decimals)
    : Math.round(value).toLocaleString();

  return `${options.prefix || ''}${formatted}${options.suffix || ''}`;
};

const CountUpNumber = ({
  end,
  prefix,
  suffix,
  decimals = 0,
  duration = 1400,
  className,
  start = false,
}: {
  end: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  duration?: number;
  className?: string;
  start?: boolean;
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!start) return;

    let frameId = 0;
    let startTime: number | null = null;

    const tick = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(end * eased);

      if (progress < 1) {
        frameId = window.requestAnimationFrame(tick);
      }
    };

    frameId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [end, duration, start]);

  return (
    <span className={className}>
      {formatCountValue(displayValue, { prefix, suffix, decimals })}
    </span>
  );
};

const renderSnapshotValue = (
  item: SnapshotItem,
  hasStartedCounts: boolean,
  className: string,
) => {
  if (item.displayValue) {
    return <span className={className}>{item.displayValue}</span>;
  }

  return (
    <CountUpNumber
      end={item.countTo || 0}
      prefix={item.prefix}
      suffix={item.suffix}
      decimals={item.decimals}
      duration={2200}
      start={hasStartedCounts}
      className={className}
    />
  );
};

const Home = () => {
  const [hasStartedHeroCounts, setHasStartedHeroCounts] = useState(false);
  const [hasStartedCounts, setHasStartedCounts] = useState(false);
  const heroDashboardRef = useRef<HTMLDivElement | null>(null);
  const snapshotSectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const node = heroDashboardRef.current;
    if (!node || hasStartedHeroCounts) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setHasStartedHeroCounts(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.3,
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [hasStartedHeroCounts]);

  useEffect(() => {
    const node = snapshotSectionRef.current;
    if (!node || hasStartedCounts) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setHasStartedCounts(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.3,
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [hasStartedCounts]);

  return (
    <SiteLayout
      title='DocMoonlight | Physician Coverage'
      description='DocMoonlight connects imaging and infusion centers with onsite, virtual, and hybrid physician coverage while helping residents find flexible moonlighting shifts.'
      keywords={[
        'physician coverage',
        'contrast supervision',
        'imaging center staffing',
        'infusion physician oversight',
        'resident moonlighting',
      ]}
      structuredData={homeStructuredData}
    >
      <section className='overflow-hidden px-4 pb-14 pt-10 sm:px-6 lg:px-8 lg:pt-14'>
        <div className='mx-auto max-w-7xl'>
          <div className='relative overflow-hidden rounded-[2.6rem] border border-slate-200 bg-[radial-gradient(circle_at_top_right,_rgba(245,158,11,0.12),_transparent_18%),radial-gradient(circle_at_bottom_left,_rgba(37,99,235,0.08),_transparent_24%),linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] px-6 py-8 shadow-[0_24px_70px_rgba(15,23,42,0.1)] md:px-10 md:py-10 lg:px-12 lg:py-12'>
            <div className='relative grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(340px,0.9fr)] lg:items-center'>
              <div className='max-w-3xl pt-2 lg:pr-6'>
                <h1 className='mt-10 text-[1.55rem] font-black leading-[1.02] tracking-[-0.05em] text-slate-950 md:text-[2.6rem]'>
                  Physician Coverage for Healthcare Facilities.
                </h1>
                <div className='mt-6 text-[1.38rem] font-black leading-[1.02] tracking-[-0.05em] text-[#d18a00] md:text-[2.25rem]'>
                  Moonlighting Made Flexible.
                </div>

                <p className='mt-8 max-w-3xl text-[0.92rem] leading-8 text-slate-600 md:text-[0.98rem]'>
                  DocMoonlight simplifies physician coverage for healthcare
                  facilities with virtual and onsite support, coordinated
                  teleradiology services, and flexible moonlighting
                  opportunities for residents and fellows.
                </p>

                <div className='mt-8 flex flex-col gap-4 sm:flex-row'>
                  <Link
                    href='#ContactPartner'
                    className='inline-flex min-h-[58px] items-center justify-center rounded-full bg-[linear-gradient(90deg,#ffb629_0%,#f59e0b_100%)] px-7 text-[0.98rem] font-black text-[#111827] shadow-[0_18px_34px_rgba(245,158,11,0.22)] transition hover:translate-y-[-1px]'
                  >
                    Request Coverage
                    <span className='ml-3 text-xl'>→</span>
                  </Link>
                  <Link
                    href='/residents'
                    className='inline-flex min-h-[58px] items-center justify-center rounded-full border border-slate-200 bg-white px-7 text-[0.98rem] font-black text-[#12315f] shadow-[0_10px_26px_rgba(15,23,42,0.08)] transition hover:bg-slate-50'
                  >
                    Join DocMoonlight
                  </Link>
                </div>
              </div>

              <div className='relative lg:max-w-[34rem] lg:justify-self-end'>
                <div
                  ref={heroDashboardRef}
                  className='p-0 sm:rounded-[1.8rem] sm:border sm:border-slate-200 sm:bg-transparent sm:p-4 sm:shadow-[0_18px_40px_rgba(15,23,42,0.1)] md:p-5'
                >
                  <div className='mt-1'>
                    <div className='grid grid-cols-2 gap-2 sm:gap-3'>
                      {heroDashboardCards.map((card) => {
                        const Icon = card.icon;
                        const toneClass =
                          card.tone === 'gold'
                            ? 'border-[#f5d6a2] bg-[#fff2d9] text-[#d18a00] shadow-[0_0_24px_rgba(255,191,63,0.12)]'
                            : card.tone === 'goldGlow'
                              ? 'border-[#f5d6a2] bg-[linear-gradient(180deg,#fff5df_0%,#fffdf8_100%)] text-[#d18a00] shadow-[0_0_28px_rgba(255,182,41,0.14)]'
                              : card.tone === 'violet'
                                ? 'border-[#ddd6fe] bg-[#f5f3ff] text-[#7c3aed]'
                                : card.tone === 'cyan'
                                  ? 'border-[#c7e9f1] bg-[#ecfeff] text-[#0891b2]'
                                  : 'border-[#d8e2f0] bg-[#eef4ff] text-[#2563eb]';

                        return (
                          <div
                            key={`${card.label}-${card.value}`}
                            className={`rounded-[1rem] border border-slate-200 bg-white p-2.5 shadow-[0_10px_22px_rgba(15,23,42,0.08)] sm:rounded-[1.3rem] sm:p-3.5 ${
                              card.wide ? 'md:col-span-1' : ''
                            }`}
                          >
                            <div className='flex items-center gap-2 sm:gap-3'>
                              <div
                                className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[0.75rem] border sm:h-11 sm:w-11 sm:rounded-[0.9rem] ${toneClass}`}
                              >
                                <Icon className='h-4 w-4 sm:h-4.5 sm:w-4.5' />
                              </div>
                              <div>
                                {renderSnapshotValue(
                                  card,
                                  hasStartedHeroCounts,
                                  'text-[0.95rem] font-black leading-none tracking-[-0.05em] text-slate-950 sm:text-[1.18rem] md:text-[1.5rem]',
                                )}
                                <div className='mt-1 text-[0.6rem] font-bold leading-4 text-slate-600 sm:text-[0.74rem] sm:leading-5'>
                                  {card.detail}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className='mt-12 mb-14 px-0 sm:px-6 lg:px-8'>
        <div className='mx-auto w-full max-w-7xl overflow-hidden bg-white px-0 py-5 shadow-none sm:rounded-[2rem] sm:shadow-[0_18px_50px_rgba(15,23,42,0.08)]'>
          <div className='px-6'>
            <div className='text-center text-sm font-bold uppercase tracking-[0.18em] text-[#14213d]'>
              Trusted imaging partners and healthcare groups we support
            </div>
          </div>
          <div className='marquee-shell mt-5'>
            <div className='marquee-track'>
              {[...marqueeItems, ...marqueeItems].map((item, index) => (
                <div
                  key={`${item.name}-${index}`}
                  className='marquee-card marquee-card-logo'
                >
                  {item.src ? (
                    <Image
                      src={item.src}
                      alt={item.name}
                      width={180}
                      height={64}
                      sizes='180px'
                      className='max-h-10 w-auto object-contain'
                    />
                  ) : (
                    <span className='text-lg font-black tracking-tight text-slate-700 md:text-2xl'>
                      {item.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className='mt-4 px-6'>
            <div className='text-center text-sm font-bold uppercase tracking-[0.18em] text-[#14213d]'>
              States represented across our doctor network
            </div>
          </div>
          <div className='marquee-shell mt-5'>
            <div className='marquee-track marquee-track-reverse marquee-track-slow'>
              {[...trainingProgramItems, ...trainingProgramItems].map(
                (item, index) => (
                  <div
                    key={`${item.name}-training-${index}`}
                    className='marquee-card marquee-card-ghost'
                  >
                    <span className='text-sm font-extrabold tracking-tight text-slate-800 md:text-base'>
                      {item.name}
                    </span>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      </div>

      <section className='overflow-hidden px-4 pb-14 pt-12 sm:px-6 lg:px-8'>
        <div className='mx-auto max-w-7xl'>
          <div className='mx-auto max-w-5xl text-center'>
            <h2 className='text-[1.75rem] font-black leading-[1] tracking-[-0.04em] text-slate-950 md:text-[3.15rem] md:leading-[0.96] md:tracking-[-0.05em]'>
              One Platform
              <span className='block'>Three Coverage Solutions</span>
            </h2>
            <p className='mx-auto mt-6 max-w-4xl text-[0.98rem] leading-8 text-slate-600 md:text-[1.08rem]'>
              DocMoonlight supports imaging centers, infusion centers, and
              physicians in training with flexible clinical coverage,
              teleradiology reading support, and moonlighting opportunities.
            </p>
          </div>

          <div className='mt-10 grid gap-5 grid-cols-1 sm:grid-cols-3'>
            {audienceOptions.map((item) => {
              const Icon = item.icon;
              return (
                <article
                  key={item.id}
                  className='group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] transition-transform duration-300 hover:-translate-y-1'
                >
                  <div className='h-2 w-full bg-[linear-gradient(90deg,#f59e0b_0%,#fbbf24_55%,#fde68a_100%)]' />
                  <div className='flex h-full flex-col p-5 md:p-6'>
                    <div className='flex items-center gap-3'>
                      <div className='relative flex h-14 w-14 items-center justify-center rounded-[1rem] bg-[linear-gradient(180deg,#1e2947_0%,#0f172a_100%)] text-[#f59e0b] shadow-[0_12px_24px_rgba(15,23,42,0.22)]'>
                        <Icon className='h-6 w-6' />
                        <span className='absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#f59e0b]' />
                      </div>
                      <div className='rounded-full border border-[#f5d6a2] bg-[#fff7eb] px-3 py-1.5 text-[0.68rem] font-bold uppercase tracking-[0.18em] text-[#d18a00]'>
                        {item.badge}
                      </div>
                    </div>

                    <h3 className='mt-8 text-[1.55rem] font-black leading-[1.08] tracking-[-0.035em] text-[#1c2746] md:text-[1.75rem]'>
                      {item.title}
                    </h3>
                    <p className='mt-4 text-[0.95rem] leading-7 text-slate-600'>
                      {item.description}
                    </p>

                    <div className='mt-auto space-y-3 pt-8'>
                      {item.points.map((point) => (
                        <div
                          key={point}
                          className='flex items-start gap-3 text-[0.94rem] font-medium leading-6 text-slate-700'
                        >
                          <span className='mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full border border-[#f4ddbb] bg-[#fff8ef] text-xs font-black text-[#e09a11]'>
                            ✓
                          </span>
                          <span>{point}</span>
                        </div>
                      ))}
                    </div>

                    <div className='mt-auto pt-8'>
                      <a
                        href={item.href}
                        className='inline-flex min-h-[52px] w-full items-center justify-center rounded-[1rem] bg-[linear-gradient(90deg,#1e2947_0%,#0b0f18_100%)] px-5 text-sm font-bold text-white shadow-[0_12px_24px_rgba(15,23,42,0.18)] transition-transform duration-300 hover:scale-[1.01]'
                      >
                        {item.ctaLabel}
                        <span className='ml-3 text-[#f59e0b]'>→</span>
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section
        ref={snapshotSectionRef}
        className='px-4 pb-14 pt-6 sm:px-6 lg:px-8'
      >
        <div className='mx-auto max-w-7xl'>
          <div className='overflow-hidden rounded-[1.8rem] sm:rounded-[2.4rem] border border-slate-200 bg-[radial-gradient(circle_at_top,_rgba(250,204,21,0.12),_transparent_24%),radial-gradient(circle_at_left,_rgba(59,130,246,0.06),_transparent_30%),linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] px-4 py-8 sm:px-5 sm:py-12 md:px-8 md:py-16 lg:px-10 shadow-[0_24px_60px_rgba(15,23,42,0.08)]'>
            <div className='text-center'>
              <div className='inline-flex items-center gap-2 sm:gap-3 rounded-full border border-[#f5d6a2] bg-white px-4 sm:px-5 py-1.5 sm:py-2 text-[0.68rem] sm:text-[0.76rem] font-bold uppercase tracking-[0.2em] sm:tracking-[0.24em] text-[#d18a00] shadow-[0_10px_24px_rgba(15,23,42,0.06)]'>
                <span className='h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-[#d7b14d]' />
                Premium Network Metrics
              </div>
              <h2 className='mx-auto mt-6 sm:mt-8 max-w-5xl text-[1.5rem] sm:text-[2rem] font-black leading-[1] sm:leading-[0.96] tracking-[-0.04em] sm:tracking-[-0.05em] text-slate-950 md:text-[3.15rem]'>
                Built for Scale.
                <span className='block text-[#51627f]'>
                  Designed for Coverage.
                </span>
              </h2>
              <p className='mx-auto mt-4 sm:mt-6 max-w-4xl text-[0.9rem] sm:text-[0.98rem] leading-7 sm:leading-8 text-slate-600 md:text-[1.08rem]'>
                Empowering healthcare facilities with a robust physician network
                and comprehensive coverage models that ensure operational
                continuity and premium patient care.
              </p>
            </div>

            <div className='mt-8 sm:mt-12 grid gap-4 sm:gap-6 lg:grid-cols-2'>
              {metricGroups.map((group) => {
                const GroupIcon = group.icon;

                return (
                  <section
                    key={group.title}
                    className='rounded-[1.4rem] sm:rounded-[2rem] border border-slate-200 bg-white p-4 sm:p-6 md:p-7 shadow-[0_18px_36px_rgba(15,23,42,0.08)]'
                  >
                    <div className='flex items-center gap-2 sm:gap-3 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[#d18a00]'>
                      <GroupIcon className='h-4 w-4 sm:h-4.5 sm:w-4.5' />
                      {group.badge}
                    </div>
                    <div className='mt-5 grid grid-cols-3 gap-1.5 sm:mt-8 sm:gap-4 md:gap-0'>
                      {group.items.map((item) => {
                        const ItemIcon = item.icon;
                        const isGold = item.tone === 'gold';

                        return (
                          <article
                            key={item.label}
                            className={`relative flex min-h-[112px] flex-col items-center justify-start px-1.5 py-3 text-center sm:min-h-[168px] sm:items-start sm:px-4 sm:py-4 sm:text-left md:px-6 ${
                              group.items.indexOf(item) < group.items.length - 1
                                ? 'border-r border-slate-100 md:border-slate-200'
                                : ''
                            }`}
                          >
                            <div
                              className={`inline-flex h-8 w-8 items-center justify-center rounded-[0.65rem] border sm:h-12 sm:w-12 sm:rounded-[0.95rem] ${
                                isGold
                                  ? 'border-[#f3d39b] bg-[#fff4df] text-[#d18a00]'
                                  : 'border-[#d8e2f0] bg-[#eef4ff] text-[#2563eb]'
                              }`}
                            >
                              <ItemIcon className='h-3.5 w-3.5 sm:h-5 sm:w-5' />
                            </div>
                            {renderSnapshotValue(
                              item,
                              hasStartedCounts,
                              `mt-3 block max-w-[9ch] text-[1.15rem] font-black leading-[1] tracking-[-0.035em] sm:mt-6 sm:max-w-[11ch] sm:text-[2.1rem] sm:tracking-[-0.05em] ${
                                isGold ? 'text-[#d18a00]' : 'text-slate-950'
                              }`,
                            )}
                            <p className='mt-1.5 max-w-[11ch] text-center text-[0.6rem] leading-3 text-slate-600 sm:mt-3 sm:max-w-[14ch] sm:text-left sm:text-[0.84rem] sm:leading-6'>
                              {item.detail}
                            </p>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className='relative overflow-hidden px-4 pb-14 pt-2 sm:px-6 lg:px-8'>
        <div className='pointer-events-none absolute inset-0'>
          <div className='absolute left-[8%] top-[6%] h-56 w-56 rounded-full bg-[radial-gradient(circle,_rgba(59,130,246,0.1)_0%,_rgba(59,130,246,0)_72%)] blur-2xl' />
          <div className='absolute right-[-4%] top-0 h-80 w-80 rounded-full bg-[radial-gradient(circle,_rgba(250,204,21,0.22)_0%,_rgba(250,204,21,0)_72%)] blur-3xl' />
          <div className='absolute bottom-[10%] left-1/4 h-72 w-72 rounded-full bg-[radial-gradient(circle,_rgba(250,204,21,0.1)_0%,_rgba(250,204,21,0)_74%)] blur-3xl' />
        </div>
        <div className='relative mx-auto max-w-[73rem]'>
          <div className='mb-10 text-center'>
            <div className='inline-flex items-center gap-2 rounded-full border border-[#f5d6a2] bg-white px-5 py-2 text-[0.72rem] font-black uppercase tracking-[0.2em] text-[#12315f] shadow-[0_10px_24px_rgba(15,23,42,0.08)]'>
              <span className='text-[#f59e0b]'>⚖</span>
              The Clear Choice
            </div>
            <h2 className='mx-auto mt-7 max-w-5xl text-[1.8rem] font-black leading-[1.08] tracking-[-0.04em] text-[#0d2b52] md:text-[3.8rem] md:tracking-[-0.06em]'>
              Why Teams Choose
              <span className='block bg-[linear-gradient(90deg,#f59e0b_0%,#ffbf1f_100%)] bg-clip-text text-transparent'>
                DocMoonlight
              </span>
            </h2>
            <p className='mx-auto mt-7 max-w-4xl text-[1rem] leading-8 text-slate-500 md:text-[1.18rem]'>
              See how our purpose-built platform and premium network outpace
              traditional staffing agencies across every metric that matters to
              your operations.
            </p>
          </div>

          <div className='mt-12 overflow-hidden rounded-[1.9rem] border border-[#f3dcb8] bg-white shadow-[0_26px_70px_rgba(15,23,42,0.1)]'>
            <div className='grid grid-cols-[0.62fr_1fr_1fr] bg-[linear-gradient(90deg,#123f73_0%,#0e4a86_100%)] px-3 py-3 md:grid-cols-[minmax(160px,0.72fr)_minmax(0,1fr)_minmax(0,1fr)] md:px-8 md:py-5'>
              <div className='text-[0.78rem] font-black uppercase tracking-[0.18em] text-white/72'></div>
              <div className='flex items-center gap-1.5 px-1 text-[0.66rem] font-black tracking-[-0.03em] text-white md:gap-3 md:px-5 md:text-[1.02rem]'>
                <span className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#355f93] text-[#ffd447] md:h-8 md:w-8'>
                  <Image
                    src='/images/footer-logo.png'
                    alt='DocMoonlight'
                    width={32}
                    height={32}
                    sizes='32px'
                    className='h-6 w-6 object-contain md:h-8 md:w-8'
                  />
                </span>
                DocMoonlight
              </div>
              <div className='flex items-center gap-1.5 px-1 text-[0.66rem] font-black tracking-[-0.03em] text-white/55 md:gap-3 md:px-5 md:text-[1.02rem]'>
                <span className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/8 text-white/55 md:h-8 md:w-8'>
                  <Building2 className='h-3 w-3 md:h-4 md:w-4' />
                </span>
                Typical Providers
              </div>
            </div>

            <div className='hidden px-6 py-5 md:px-9 md:py-6'>
              <div className='hidden grid-cols-[minmax(190px,0.8fr)_minmax(0,1fr)_minmax(0,1fr)] border-b border-slate-200 pb-4 md:grid'>
                <div />
                <div className='px-5 text-center text-[1.15rem] font-black tracking-[-0.03em] text-[#16375f]'>
                  DocMoonlight
                </div>
                <div className='px-5 text-center text-[1.15rem] font-black tracking-[-0.03em] text-slate-400'>
                  Typical Providers
                </div>
              </div>

              <div className='mt-2 divide-y divide-slate-100 md:mt-0'>
                {comparisonRows.map((row) => (
                  <div
                    key={row.category}
                    className='grid gap-4 py-4 md:grid-cols-[minmax(190px,0.8fr)_minmax(0,1fr)_minmax(0,1fr)] md:items-start md:gap-6'
                  >
                    <div className='pt-1'>
                      <div className='text-xl font-black tracking-[-0.03em] text-[#16375f] md:text-[1.08rem]'>
                        {row.category}
                      </div>
                    </div>

                    <div>
                      <div className='mb-3 text-xs font-bold uppercase tracking-[0.18em] text-[#16375f] md:hidden'>
                        DocMoonlight
                      </div>
                      <div className='flex items-start gap-3 rounded-[1.1rem] border border-[#f6dfb8] bg-[#fff9ec] px-4 py-4 shadow-[0_8px_18px_rgba(245,158,11,0.08)]'>
                        <span className='mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#ff8f00] text-xs font-black text-white'>
                          ✓
                        </span>
                        <span className='text-[0.95rem] font-bold leading-6 text-[#22324c]'>
                          {row.docMoonlight}
                        </span>
                      </div>
                    </div>

                    <div>
                      <div className='mb-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-400 md:hidden'>
                        Typical Providers
                      </div>
                      <div className='flex items-start gap-3 px-1 py-1'>
                        <span className='mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center text-base font-black text-slate-400'>
                          ⚠
                        </span>
                        <span className='text-[0.95rem] leading-6 text-slate-500'>
                          {row.others}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className='divide-y divide-[#edf1f6]'>
              {comparisonSections.map((section) => {
                const SectionIcon = section.icon;
                const sectionIconClasses =
                  section.tone === 'gold'
                    ? 'bg-[linear-gradient(180deg,#ffbf29_0%,#f59e0b_100%)] text-white shadow-[0_10px_18px_rgba(245,158,11,0.28)]'
                    : 'bg-[linear-gradient(180deg,#0f4b88_0%,#123f73_100%)] text-[#ffd447] shadow-[0_10px_18px_rgba(18,63,115,0.24)]';

                return (
                  <div key={section.title}>
                    <div className='border-b border-[#edf1f6] px-3 py-3 md:px-8 md:py-5'>
                      <div className='flex items-center gap-2 md:gap-4'>
                        <span
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-[0.7rem] md:h-10 md:w-10 md:rounded-[0.85rem] ${sectionIconClasses}`}
                        >
                          <SectionIcon className='h-3.5 w-3.5 md:h-4.5 md:w-4.5' />
                        </span>
                        <div className='text-[0.88rem] font-black tracking-[-0.03em] text-[#12315f] md:text-[1.24rem]'>
                          {section.title}
                        </div>
                      </div>
                    </div>

                    <div className='divide-y divide-[#edf1f6]'>
                      {section.rows.map((row) => (
                        <div
                          key={row.category}
                          className='grid grid-cols-[0.62fr_1fr_1fr] items-start gap-2 px-3 py-3 md:grid-cols-[minmax(160px,0.72fr)_minmax(0,1fr)_minmax(0,1fr)] md:gap-6 md:px-8 md:py-4'
                        >
                          <div className='text-[0.68rem] font-black leading-4 tracking-[-0.02em] text-[#12315f] md:text-[0.95rem] md:leading-normal'>
                            {row.category}
                          </div>

                          <div>
                            <div className='flex items-start gap-1.5 md:gap-3'>
                              <span className='mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-[#ffb11a] text-white shadow-[0_6px_12px_rgba(245,158,11,0.22)] md:h-5 md:w-5'>
                                <CircleCheckBig className='h-2.5 w-2.5 md:h-3.5 md:w-3.5' />
                              </span>
                              <span className='text-[0.64rem] font-bold leading-4 text-[#22324c] md:text-[0.92rem] md:leading-7'>
                                {row.docMoonlight}
                              </span>
                            </div>
                          </div>

                          <div>
                            <div className='flex items-start gap-1.5 md:gap-3'>
                              <span className='mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-[#c4cad5] text-white md:h-5 md:w-5'>
                                <CircleAlert className='h-2.5 w-2.5 md:h-3.5 md:w-3.5' />
                              </span>
                              <span className='text-[0.64rem] leading-4 text-slate-500 md:text-[0.92rem] md:leading-7'>
                                {row.others}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <CoverageRequestForm />
    </SiteLayout>
  );
};

export default Home;
